/* ==========================================================================
   <sura-facets>
   The form is a plain GET form posting back to the collection URL, so
   filtering works with JavaScript disabled. This element only removes the
   need to press "Apply".
   ========================================================================== */

class SuraFacets extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-facets-form]');
    this.panel = this.querySelector('[data-facets-panel]');
    this.overlay = this.querySelector('[data-facets-overlay]');
    this.openBtn = this.querySelector('[data-facets-open]');
    this.closeBtn = this.querySelector('[data-facets-close]');
    this.sortSelect = this.querySelector('[data-facets-sort]');

    this.bindForm();
    this.bindSort();
    this.bindDrawer();

    document.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    document.body.classList.remove('sura-no-scroll');
  }

  onKeydown = (event) => {
    if (event.key === 'Escape') this.closeDrawer();
  };

  /* ------------------------------------------------------------------ Form */

  bindForm() {
    if (!this.form) return;

    this.form.addEventListener('change', (event) => {
      // Price inputs fire change on every keystroke in some browsers, so they
      // are handled separately with a debounce.
      if (event.target.matches('[data-facets-price]')) return;
      this.submit();
    });

    let priceTimer;
    this.form.querySelectorAll('[data-facets-price]').forEach((input) => {
      input.addEventListener('input', () => {
        clearTimeout(priceTimer);
        priceTimer = setTimeout(() => {
          if (this.validatePrices()) this.submit();
        }, 700);
      });
    });

    // Enter inside a price field should filter, not reload a blank form.
    this.form.addEventListener('submit', (event) => {
      if (!this.validatePrices()) event.preventDefault();
    });
  }

  /* Stops "from" exceeding "to", which would return nothing. */
  validatePrices() {
    const inputs = Array.from(this.form.querySelectorAll('[data-facets-price]'));
    if (inputs.length < 2) return true;

    const [minInput, maxInput] = inputs;
    const min = parseFloat(minInput.value);
    const max = parseFloat(maxInput.value);

    if (!Number.isNaN(min) && !Number.isNaN(max) && min > max) {
      maxInput.setCustomValidity('The higher price must be more than the lower price');
      maxInput.reportValidity();
      return false;
    }

    maxInput.setCustomValidity('');
    return true;
  }

  submit() {
    if (!this.form) return;
    // Empty fields would otherwise appear in the URL as bare parameters.
    this.form.querySelectorAll('input[type="number"]').forEach((input) => {
      if (input.value === '') input.disabled = true;
    });
    this.form.submit();
  }

  /* ------------------------------------------------------------------ Sort */

  bindSort() {
    if (!this.sortSelect) return;

    this.sortSelect.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', this.sortSelect.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  }

  /* ---------------------------------------------------------------- Drawer */

  bindDrawer() {
    if (this.openBtn) this.openBtn.addEventListener('click', () => this.openDrawer());
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.closeDrawer());
    if (this.overlay) this.overlay.addEventListener('click', () => this.closeDrawer());
  }

  get isDrawer() {
    // Matches the breakpoint in sura-collection-grid.css
    return window.matchMedia('(max-width: 989px)').matches;
  }

  openDrawer() {
    if (!this.panel) return;
    this.classList.add('is-open');
    if (this.overlay) this.overlay.hidden = false;
    if (this.openBtn) this.openBtn.setAttribute('aria-expanded', 'true');
    document.body.classList.add('sura-no-scroll');

    const first = this.panel.querySelector('input, button, a, summary');
    if (first) first.focus();
  }

  closeDrawer() {
    if (!this.panel || !this.classList.contains('is-open')) return;
    this.classList.remove('is-open');
    if (this.overlay) this.overlay.hidden = true;
    if (this.openBtn) {
      this.openBtn.setAttribute('aria-expanded', 'false');
      if (this.isDrawer) this.openBtn.focus();
    }
    document.body.classList.remove('sura-no-scroll');
  }
}

if (!customElements.get('sura-facets')) {
  customElements.define('sura-facets', SuraFacets);
}
